import { defineModule } from "direct-vuex";
import { Contractor } from "@/models/api/Contractor";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { RemoveFileCommand } from "@/models/api/costDocumentsOther/editor/commands/RemoveFileCommand";
import { RemoveFilesCommand } from "@/models/api/costDocumentsOther/editor/commands/RemoveFilesCommand";
import { ChangeContractorCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeContractorCommand";
import { RemoveContractorCommand } from "@/models/api/costDocumentsOther/editor/commands/RemoveContractorCommand";
import { ChangeDocumentNumberCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeDocumentNumberCommand";
import { ChangeDescriptionCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeDescriptionCommand";
import { ChangeIssuePlaceCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeIssuePlaceCommand";
import { ChangeFormOfPaymentCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeFormOfPaymentCommand";
import { ChangeDocumentTypeCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeDocumentTypeCommand";
import { ChangeSaleDateCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeSaleDateCommand";
import { ChangeIssueDateCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeIssueDateCommand";
import { ChangeReceiptDateCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeReceiptDateCommand";
import { ChangePaymentDateCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangePaymentDateCommand";
import { ChangeExchangeDateCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeExchangeDateCommand";
import { ChangeExchangeRateCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeExchangeRateCommand";
import { ChangeCurrencyCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeCurrencyCommand";
import { AddItemCommand } from "@/models/api/costDocumentsOther/editor/commands/AddItemCommand";
import { RemoveItemCommand } from "@/models/api/costDocumentsOther/editor/commands/RemoveItemCommand";
import { ChangeServiceTypeCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeServiceTypeCommand";
import { ChangeQuantityCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeQuantityCommand";
import { ChangeNetPriceCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeNetPriceCommand";
import { ChangeGrossCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeGrossCommand";
import { CheckBeforeSaveCommand } from "@/models/api/costDocumentsOther/editor/commands/CheckBeforeSaveCommand";
import { AddAttributeCommand } from "@/models/api/costDocumentsOther/editor/commands/AddAttributeCommand";
import { ChangeAttributeCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeAttributeCommand";
import { ChangeVATAmountCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeVATAmountCommand";
import { ChangeStatusCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeStatusCommand";
import { RemoveContractComponentCommand } from "@/models/api/costDocumentsOther/editor/commands/RemoveContractComponentCommand";
import { ChangeContractComponentCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeContractComponentCommand";
import { ChangeVATCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeVATCommand";
import { ChangeBankAccountNumberCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeBankAccountNumberCommand";
import { ChangeCreditCardNumberCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeCreditCardNumberCommand";
import { ChangeItemNameCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeItemNameCommand";
import { ChangeServiceTypeForAllItemsCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeServiceTypeForAllItemsCommand";
import { RemoveAttributeCommand } from "@/models/api/costDocumentsOther/editor/commands/RemoveAttributeCommand";
import { ChangeCorrectedDocumentNumberCommand } from "@/models/api/costDocumentsOther/editor/commands/ChangeCorrectedDocumentNumberCommand";

const CostDocumentsOtherEditorCommandsModule = defineModule({
  state: {},
  namespaced: true as const,
  getters: {},
  mutations: {},
  actions: {
    async removeFile(context, value: number) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new RemoveFileCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async removeFiles(context) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new RemoveFilesCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async selectContractor(context, param: Contractor) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeContractorCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          {
            shortName: param.shortName as string,
            importedContractorId: param.id,
            city: param.city as string,
            country: param.country as string,
            surname: param.surname as string,
            isIndividual: param.isIndividual,
            name: param.name as string,
            streetAndNumber: param.streetAndNumber as string,
            tin: param.tin as string,
            postalCode: param.postalCode as string,
            nationalBusinessRegistryNumber: param.nationalBusinessRegistryNumber as string,
            relationType: rootState.costDocumentsOther.editor.currentContractorType,
          }
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async removeContractor(context) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new RemoveContractorCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          rootState.costDocumentsOther.editor.currentContractorType
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeDocumentNumber(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeDocumentNumberCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeBankAccount(context, value: number | null) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeBankAccountNumberCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeCreditCardNumber(context, value: number | null) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeCreditCardNumberCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeCorrectedDocumentNumber(context, value: string | null) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeCorrectedDocumentNumberCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeDescription(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeDescriptionCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeIssuePlace(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeIssuePlaceCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeFormOfPayment(context, value: number) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeFormOfPaymentCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeDocumentType(context, value: number) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeDocumentTypeCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeIssueDate(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeIssueDateCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeSaleDate(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeSaleDateCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeReceiptDate(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeReceiptDateCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changePaymentDate(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangePaymentDateCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeExchangeDate(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeExchangeDateCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeExchangeRate(context, value: number) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeExchangeRateCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeCurrency(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeCurrencyCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async addItem(context) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new AddItemCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async removeItem(context, tmpId: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new RemoveItemCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          tmpId
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeItemName(context, param: { tmpId: string; value: string }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeItemNameCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          param.tmpId,
          param.value,
          false
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeServiceType(context, param: { tmpId: string; value: number; calculate: boolean }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeServiceTypeCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          param.tmpId,
          param.value,
          param.calculate
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeServiceTypeForAllItems(context, param: number | null) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId && param) {
        const command = new ChangeServiceTypeForAllItemsCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          param
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeVAT(context, param: { tmpId: string; value: string; calculate: boolean }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeVATCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          param.tmpId,
          param.value,
          param.calculate
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeQuantity(context, param: { tmpId: string; value: number; calculate: boolean }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeQuantityCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          param.tmpId,
          param.value,
          param.calculate
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeNetPrice(context, param: { tmpId: string; value: number; calculate: boolean }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeNetPriceCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          param.tmpId,
          param.value,
          param.calculate
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeGross(context, param: { tmpId: string; value: number; calculate: boolean }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeGrossCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          param.tmpId,
          param.value,
          param.calculate
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeVATAmount(context, param: { tmpId: string; value: number; calculate: boolean }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeVATAmountCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          param.tmpId,
          param.value,
          param.calculate
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async checkBeforeSave(context) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new CheckBeforeSaveCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async addAttribute(context, param: { prototypeId: number; value: string }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new AddAttributeCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          rootState.costDocumentsOther.editor.costDocumentItemId,
          param.prototypeId,
          param.value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeAttribute(context, param: { attributeTmpId: string; value: string }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeAttributeCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          rootState.costDocumentsOther.editor.costDocumentItemId,
          param.attributeTmpId,
          param.value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async removeAttribute(context, attributeTmpId: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new RemoveAttributeCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          rootState.costDocumentsOther.editor.costDocumentItemId,
          attributeTmpId
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeStatus(context, value: number) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeStatusCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          value
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async changeContractComponent(context, componentId: number) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new ChangeContractComponentCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId,
          componentId
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
    async removeContractComponent(context) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.costDocumentsOther.editor.sessionId && rootState.costDocumentsOther.editor.operationId) {
        const command = new RemoveContractComponentCommand(
          rootState.costDocumentsOther.editor.sessionId,
          rootState.costDocumentsOther.editor.operationId
        );
        await rootDispatch.costDocumentsOther.editor.addCommandToQueue(command);
      }
    },
  },
});

export default CostDocumentsOtherEditorCommandsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, CostDocumentsOtherEditorCommandsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, CostDocumentsOtherEditorCommandsModule);
